/**
 * Renders HTML from a rich-text editor when content looks like markup; otherwise plain text.
 * Sanitizes editor output in the browser (SSR strips tags to plain text).
 */

export function looksLikeHtml(s: string): boolean {
  return /<[a-z][\s\S]*>/i.test(s.trim());
}

/** Browser-safe sanitization for rich-text editor output (TipTap/HTML). */
export function sanitizeRichTextHtml(input: string): string {
  const t = input.trim();
  if (!t) return t;
  if (typeof window === "undefined") {
    return t.replace(/<[^>]*>/g, "").trim();
  }
  try {
    const doc = new DOMParser().parseFromString(t, "text/html");
    for (const el of doc.querySelectorAll(
      "script,style,iframe,object,embed,link,meta,form",
    )) {
      el.remove();
    }
    doc.body.querySelectorAll("*").forEach((el) => {
      for (const attr of [...el.attributes]) {
        const n = attr.name.toLowerCase();
        if (n.startsWith("on") || n === "formaction") {
          el.removeAttribute(attr.name);
        }
        if (n === "href" && /^\s*javascript:/i.test(attr.value)) {
          el.removeAttribute(attr.name);
        }
      }
    });
    return doc.body.innerHTML;
  } catch {
    return "";
  }
}

type RichTextOrPlainProps = {
  text: string;
  className?: string;
};

export function RichTextOrPlain({ text, className }: RichTextOrPlainProps) {
  const trimmed = text.trim();
  if (!trimmed) return null;

  if (!looksLikeHtml(text)) {
    return <span className={className}>{text}</span>;
  }

  const safeHtml = sanitizeRichTextHtml(text);
  const fallbackPlain = text.replace(/<[^>]*>/g, "").trim();
  if (!safeHtml.trim() && fallbackPlain) {
    return <span className={className}>{fallbackPlain}</span>;
  }
  if (!safeHtml.trim()) return null;

  return (
    <span
      className={`rich-text-inline [&_ol]:my-0 [&_p:first-child]:mt-0 [&_p]:m-0 [&_ul]:my-0 ${className ?? ""}`}
      // biome-ignore lint/security/noDangerouslySetInnerHtml: HTML is sanitized via DOMParser (scripts/iframes/on* removed) before set
      dangerouslySetInnerHTML={{ __html: safeHtml }}
    />
  );
}

type RichTextBlockProps = {
  text: string;
  className?: string;
};

/** Block-level rich text (e.g. JD items with &lt;p&gt;/&lt;ul&gt; from editor). Sanitized like {@link RichTextOrPlain}. */
export function RichTextBlock({ text, className }: RichTextBlockProps) {
  const trimmed = text.trim();
  if (!trimmed) return null;

  if (!looksLikeHtml(text)) {
    return (
      <div className={className}>
        <p className="whitespace-pre-wrap">{text}</p>
      </div>
    );
  }

  const safeHtml = sanitizeRichTextHtml(text);
  const fallbackPlain = text.replace(/<[^>]*>/g, "").trim();
  if (!safeHtml.trim() && fallbackPlain) {
    return (
      <div className={className}>
        <p className="whitespace-pre-wrap">{fallbackPlain}</p>
      </div>
    );
  }
  if (!safeHtml.trim()) return null;

  return (
    <div
      className={`rich-text-block prose prose-sm dark:prose-invert max-w-none text-muted-foreground [&_li]:my-0.5 [&_ol]:my-2 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0 [&_p]:my-1 [&_ul]:my-2 ${className ?? ""}`}
      // biome-ignore lint/security/noDangerouslySetInnerHtml: sanitized via sanitizeRichTextHtml before set
      dangerouslySetInnerHTML={{ __html: safeHtml }}
    />
  );
}
